import React, { useState, useEffect, useRef } from "react";
import { apiGet, apiPost } from "../../services/api";
import { DiagnosisOption, VisitNotesResponse } from "./types";
import { diagnosisLabel } from "./utils";
import SkeletonLoader from "../common/SkeletonLoader";
import { useDebounce } from "../../hooks/useDebounce";

interface DiagnosisPanelProps {
    visitIdValue: number | null;
    patientIdValue: string;
    diagnosisOptions: DiagnosisOption[];
    setDiagnosisOptions: React.Dispatch<React.SetStateAction<DiagnosisOption[]>>;
    diagnosisMasterOptions: DiagnosisOption[];
    commonDiagnoses: DiagnosisOption[];
    visitDiagnoses: DiagnosisOption[];
    setVisitDiagnoses: React.Dispatch<React.SetStateAction<DiagnosisOption[]>>;
    showToast: (_args: { message: string; type: "success" | "error" | "info" | "warning" }) => void;
}

export default function DiagnosisPanel({
    visitIdValue,
    patientIdValue,
    diagnosisOptions,
    setDiagnosisOptions,
    diagnosisMasterOptions,
    commonDiagnoses,
    visitDiagnoses,
    setVisitDiagnoses,
    showToast,
}: DiagnosisPanelProps) {
    const [diagnosisInput, setDiagnosisInput] = useState("");
    const [diagnosisTooth, setDiagnosisTooth] = useState("");
    const [savingDiagnosis, setSavingDiagnosis] = useState(false);

    const [clinicalNote, setClinicalNote] = useState("");
    const [noteHabits, setNoteHabits] = useState("");
    const [noteObservation, setNoteObservation] = useState("");
    const [loadingNote, setLoadingNote] = useState(false);
    const [savingNote, setSavingNote] = useState(false);
    const noteSavedSnapshotRef = useRef({ complaints: "", habits: "", observation: "" });
    const noteSaveTimerRef = useRef<number | null>(null);
    
    // Quick chips
    const quickDiagnosisRows = (commonDiagnoses.length > 0 ? commonDiagnoses : diagnosisMasterOptions).slice(0, 8);
    const visibleDiagnosisOptions = diagnosisOptions.slice(0, 12);

    const debouncedDiagnosisInput = useDebounce(diagnosisInput, 260);

    // Diagnosis Auto-search
    useEffect(() => {
        const q = debouncedDiagnosisInput.trim().toLowerCase().replace(/\s+/g, " ");
        if (q.length < 2) {
            setDiagnosisOptions(diagnosisMasterOptions);
            return;
        }

        const fetchDiagnoses = async () => {
            const localMatches = diagnosisMasterOptions.filter((item) =>
                diagnosisLabel(item).toLowerCase().replace(/\s+/g, " ").includes(q)
            );
            try {
                const res = await apiGet(`/api/doctor/diagnoses/search?q=${encodeURIComponent(q)}`);
                const remote = Array.isArray(res) ? (res as DiagnosisOption[]) : [];
                const seen = new Set<string>();
                const merged: DiagnosisOption[] = [];
                for (const row of [...localMatches, ...remote]) {
                    const lbl = diagnosisLabel(row).toLowerCase().replace(/\s+/g, " ");
                    if (!seen.has(lbl)) {
                        seen.add(lbl);
                        merged.push(row);
                    }
                }
                setDiagnosisOptions(merged);
            } catch {
                setDiagnosisOptions(localMatches);
            }
        };

        void fetchDiagnoses();
    }, [debouncedDiagnosisInput, diagnosisMasterOptions, setDiagnosisOptions]);

    // Load Notes
    useEffect(() => {
        if (!visitIdValue) {
            setClinicalNote("");
            setNoteHabits("");
            setNoteObservation("");
            noteSavedSnapshotRef.current = { complaints: "", habits: "", observation: "" };
            return;
        }
        let mounted = true;
        const loadNote = async () => {
            setLoadingNote(true);
            try {
                const res = (await apiGet(`/api/doctor/visit-notes/${visitIdValue}`)) as VisitNotesResponse;
                if (!mounted) return;
                const nextNote = String(res?.complaints || res?.notes || "");
                const nextHabits = String(res?.habits || "");
                const nextObservation = String(res?.observation || "");
                setClinicalNote(nextNote);
                setNoteHabits(nextHabits);
                setNoteObservation(nextObservation);
                noteSavedSnapshotRef.current = {
                    complaints: nextNote,
                    habits: nextHabits,
                    observation: nextObservation,
                };
            } catch {
                if (!mounted) return;
                setClinicalNote("");
                setNoteHabits("");
                setNoteObservation("");
                noteSavedSnapshotRef.current = { complaints: "", habits: "", observation: "" };
            } finally {
                if (mounted) setLoadingNote(false);
            }
        };
        void loadNote();
        return () => { mounted = false; };
    }, [visitIdValue]);

    // Auto-save Notes (Optimistic UI Approach)
    useEffect(() => {
        if (!visitIdValue || !patientIdValue) return;
        const prev = noteSavedSnapshotRef.current;
        const unchanged =
            prev.complaints === clinicalNote &&
            prev.habits === noteHabits &&
            prev.observation === noteObservation;
        if (unchanged) return;
        if (noteSaveTimerRef.current) window.clearTimeout(noteSaveTimerRef.current);

        noteSaveTimerRef.current = window.setTimeout(async () => {
            setSavingNote(true);
            try {
                await apiPost("/api/doctor/visit-notes", {
                    visit_id: visitIdValue,
                    patient_id: patientIdValue,
                    complaints: clinicalNote,
                    habits: noteHabits,
                    observation: noteObservation,
                });
                noteSavedSnapshotRef.current = {
                    complaints: clinicalNote,
                    habits: noteHabits,
                    observation: noteObservation,
                };
            } catch {
                // Silent block for autosave
            } finally {
                setSavingNote(false);
            }
        }, 550);

        return () => {
            if (noteSaveTimerRef.current) window.clearTimeout(noteSaveTimerRef.current);
        };
    }, [clinicalNote, noteHabits, noteObservation, visitIdValue, patientIdValue]);

    const hasUnsavedNotes =
        noteSavedSnapshotRef.current.complaints !== clinicalNote ||
        noteSavedSnapshotRef.current.habits !== noteHabits ||
        noteSavedSnapshotRef.current.observation !== noteObservation;

    const handleAddDiagnosis = async (labelOverride?: string) => {
        const label = (labelOverride || diagnosisInput).trim();
        if (!label) {
            showToast({ message: "Select or type a diagnosis.", type: "warning" });
            return;
        }

        setSavingDiagnosis(true);
        let tempId = Date.now();
        
        // Optimistic UI update
        setVisitDiagnoses((prev) => [
            ...prev,
            { id: tempId, diagnosis_id: tempId, diagnosis: label, description: label, tooth_number: diagnosisTooth },
        ]);

        try {
            const res = await apiPost("/api/doctor/diagnoses", {
                visit_id: visitIdValue,
                diagnosis: label,
                tooth_number: diagnosisTooth.trim() || null,
            });
            // Replace optimistic ID with DB ID
            const newId = (res as { diagnosis_id: number }).diagnosis_id;
            setVisitDiagnoses((prev) =>
                prev.map((d) => (d.id === tempId ? { ...d, id: newId, diagnosis_id: newId } : d))
            );
            setDiagnosisInput("");
            setDiagnosisTooth("");
            setDiagnosisOptions(diagnosisMasterOptions);
        } catch {
            // Revert optimistic insert
            setVisitDiagnoses((prev) => prev.filter((d) => d.id !== tempId));
            showToast({ message: "Failed to add diagnosis.", type: "error" });
        } finally {
            setSavingDiagnosis(false);
        }
    };

    const handleRemoveDiagnosis = async (id?: number | string) => {
        if (!id) return;
        const backup = [...visitDiagnoses];
        // Optimistic UI
        setVisitDiagnoses((prev) => prev.filter((d) => (d.diagnosis_id || d.id) !== id));

        try {
            await apiPost(`/api/doctor/diagnoses/${id}/delete`, {});
        } catch {
            setVisitDiagnoses(backup); // Revert on failure
            showToast({ message: "Failed to delete diagnosis.", type: "error" });
        }
    };

    if (!visitIdValue) {
        return (
            <div className="mod-standalone-window">
                <div className="mod-empty-state" style={{ height: '300px', border: 'none' }}>
                    <div className="mod-empty-icon-wrapper" style={{ background: '#e0f2fe', color: '#0ea5e9' }}>
                        <span className="mod-empty-icon" style={{ opacity: 1 }}>🩺</span>
                    </div>
                    <h2>No Active Visit</h2>
                    <p>Select or start a visit to record examinations, notes, and diagnoses.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mod-standalone-window" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Header */}
            <div className="mod-window-header" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
                <div>
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        🩺 Clinical Examination & Notes
                    </h2>
                    <p className="mod-text-muted">Record chief complaints, observations, and clinical diagnoses.</p>
                </div>
                
                {/* Autosave Indicator */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: 600, color: hasUnsavedNotes || savingNote ? '#d97706' : '#10b981', background: hasUnsavedNotes || savingNote ? '#fef3c7' : '#d1fae5', padding: '4px 10px', borderRadius: '20px' }}>
                    <div className={`mod-status-dot ${hasUnsavedNotes || savingNote ? '' : 'online'}`} style={{ position: 'relative', border: 'none', background: 'currentColor', width: '8px', height: '8px' }}></div>
                    {savingNote ? "Saving..." : hasUnsavedNotes ? "Unsaved edits" : "All notes saved"}
                </div>
            </div>

            {/* Clinical Notes Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--mod-text-main)' }}>Chief Complaint</label>
                    <textarea 
                        value={clinicalNote}
                        onChange={(e) => setClinicalNote(e.target.value)}
                        placeholder="Patient's primary reason for visit..."
                        style={{ width: '100%', minHeight: '80px', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#f8fafc', resize: 'vertical', fontSize: '0.9rem', fontFamily: 'inherit' }}
                    />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--mod-text-main)' }}>Clinical Observations</label>
                    <textarea 
                        value={noteObservation}
                        onChange={(e) => setNoteObservation(e.target.value)}
                        placeholder="Doctor's findings..."
                        style={{ width: '100%', minHeight: '80px', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#f8fafc', resize: 'vertical', fontSize: '0.9rem', fontFamily: 'inherit' }}
                    />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--mod-text-main)' }}>Medical / Dental Habits</label>
                    <textarea 
                        value={noteHabits}
                        onChange={(e) => setNoteHabits(e.target.value)}
                        placeholder="Relevant history or habits..."
                        style={{ width: '100%', minHeight: '80px', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#f8fafc', resize: 'vertical', fontSize: '0.9rem', fontFamily: 'inherit' }}
                    />
                </div>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '4px 0' }} />

            {/* Diagnosis Entry */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <label style={{ fontSize: '0.95rem', fontWeight: 800, color: 'var(--mod-text-main)' }}>Add Diagnosis</label>
                
                {/* Quick Chips */}
                {quickDiagnosisRows.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '4px' }}>
                        {quickDiagnosisRows.map((opt) => (
                            <button 
                                key={opt.id}
                                onClick={() => {
                                    setDiagnosisInput(diagnosisLabel(opt));
                                }}
                                style={{ padding: '6px 12px', borderRadius: '20px', background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#475569', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
                                onMouseOver={(e) => { e.currentTarget.style.background = '#e0f2fe'; e.currentTarget.style.borderColor = '#bae6fd'; e.currentTarget.style.color = '#0369a1'; }}
                                onMouseOut={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#475569'; }}
                            >
                                + {diagnosisLabel(opt)}
                            </button>
                        ))}
                    </div>
                )}

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                   <div style={{ flex: '1 1 300px', position: 'relative' }}>
                        <input
                            style={{ width: '100%', padding: '12px 16px', border: '1px solid #94a3b8', borderRadius: '10px', fontSize: '0.95rem', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)' }}
                            value={diagnosisInput}
                            onChange={(e) => setDiagnosisInput(e.target.value)}
                            placeholder="Type diagnosis manually or select from above..."
                        />
                        {diagnosisInput.trim().length >= 1 && visibleDiagnosisOptions.length > 0 && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 6px)',
                                    left: 0,
                                    right: 0,
                                    zIndex: 30,
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '10px',
                                    background: '#ffffff',
                                    boxShadow: '0 12px 20px rgba(15,23,42,0.10)',
                                    maxHeight: '260px',
                                    overflowY: 'auto',
                                }}
                            >
                                {visibleDiagnosisOptions.map((opt) => {
                                    const label = diagnosisLabel(opt);
                                    if (!label) return null;
                                    return (
                                        <button
                                            key={String(opt.id || opt.diagnosis_id || label)}
                                            type="button"
                                            onClick={() => setDiagnosisInput(label)}
                                            style={{
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '10px 12px',
                                                border: 'none',
                                                borderBottom: '1px solid #f1f5f9',
                                                background: '#fff',
                                                cursor: 'pointer',
                                                fontSize: '0.9rem',
                                                color: '#0f172a',
                                            }}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                   </div>
                   <div style={{ width: '120px' }}>
                        <input
                            style={{ width: '100%', padding: '12px 16px', border: '1px solid #94a3b8', borderRadius: '10px', fontSize: '0.95rem', textAlign: 'center' }}
                            value={diagnosisTooth}
                            onChange={(e) => setDiagnosisTooth(e.target.value)}
                            placeholder="Tooth #"
                        />
                   </div>
                   <button 
                       onClick={() => handleAddDiagnosis()} 
                       disabled={savingDiagnosis || !diagnosisInput.trim()}
                       style={{ padding: '12px 24px', borderRadius: '10px', background: 'linear-gradient(135deg, var(--mod-sky), #3b82f6)', color: '#fff', border: 'none', fontWeight: 700, fontSize: '0.95rem', cursor: (!diagnosisInput.trim() || savingDiagnosis) ? 'not-allowed' : 'pointer', opacity: (!diagnosisInput.trim() || savingDiagnosis) ? 0.6 : 1, transition: 'transform 0.2s, box-shadow 0.2s', boxShadow: '0 4px 6px rgba(14, 165, 233, 0.2)' }}
                    >
                       {savingDiagnosis ? 'Adding...' : 'Add Record'}
                   </button>
                </div>
            </div>

            {/* Diagnoses List (Modern Cards) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
                <h3 style={{ fontSize: '0.9rem', color: 'var(--mod-text-muted)', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recorded Diagnoses ({visitDiagnoses.length})</h3>
                
                {visitDiagnoses.length === 0 ? (
                    <div style={{ padding: '24px', textAlign: 'center', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1', color: '#94a3b8', fontSize: '0.9rem' }}>
                        No diagnoses recorded yet for this visit.
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {visitDiagnoses.map((row) => {
                            const label = diagnosisLabel(row);
                            return (
                                <div key={row.diagnosis_id || row.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 2px 4px rgba(15,23,42,0.02)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#e0f2fe', color: '#0369a1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.85rem' }}>
                                            {row.tooth_number ? `#${row.tooth_number}` : 'Gen'}
                                        </div>
                                        <div>
                                            <h4 style={{ margin: '0 0 2px 0', fontSize: '1rem', color: 'var(--mod-text-main)' }}>{label}</h4>
                                            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--mod-text-muted)' }}>
                                                Added on {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </p>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => handleRemoveDiagnosis(row.diagnosis_id || row.id)}
                                        style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
                                        onMouseOver={(e) => e.currentTarget.style.background = '#fee2e2'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                        title="Remove Diagnosis"
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
